import React, { useState } from 'react';
import _get from 'lodash/get';
import { client } from 'cccisd-apollo';
import axios from 'cccisd-axios';
import IconCheck from 'cccisd-icons/checkmark4';
import IconCross from 'cccisd-icons/cross4';
import IconSpinner from 'cccisd-icons/spinner3';
import IconText from 'cccisd-icons/file-text2';
import Loader from 'cccisd-loader';
import Modal from 'cccisd-modal';
import Spinner from 'cccisd-spinner';
import Table from 'cccisd-table';
import Tooltip from 'cccisd-tooltip';

import TestRunQuery from './testRuns.graphql';
import style from './style.css';

let reloadTimer = 0;
const reloadTimeout = 5000;

function stripAnsi(str) {
    if (typeof str !== 'string') return str;
    // eslint-disable-next-line no-control-regex
    return str.replace(/(?:\x1b\[|(?<!\w)\[)[\d;]+m/g, '');
}

function PreOutput({ value }) {
    const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
    return (
        <pre
            className="p-2 font-monospace bg-body-secondary rounded"
            style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: '70vh', overflowY: 'auto' }}
        >
            {stripAnsi(text)}
        </pre>
    );
}

function renderSpinner() {
    return (
        <span style={{ height: '18px', width: '28px', display: 'inline-block' }}>
            <Spinner options={{ position: 'relative', top: '0.7em', left: '0.7em' }} />
        </span>
    );
}

export const JobStatus = ({ value, loadData }) => {
    if (parseInt(value, 10) < 2) {
        if (reloadTimer === 0) {
            reloadTimer = setTimeout(() => {
                loadData();
                reloadTimer = 0;
            }, reloadTimeout);
        }
    }
    if (parseInt(value, 10) === 1) {
        return (
            <>
                {renderSpinner()}
                <span>Processing</span>
            </>
        );
    }
    if (parseInt(value, 10) === 2) {
        return '✅ Success';
    }
    if (parseInt(value, 10) === 3) {
        return '❌ Failed';
    }
    return (
        <>
            {renderSpinner()}
            <span>Queued</span>
        </>
    );
};

function AllTestRuns({ data, app, tag }) {
    if (!data || !Array.isArray(data)) {
        return <Loader loading type="inline" />;
    }

    return (
        <Table
            rowKey="testRunKey"
            name="testRuns"
            orderBy="testRunKey"
            isAscentOrder={false}
            showPerPageOptions={false}
            noRecordsMessage="No test results"
            saveState={false}
            data={data}
            columns={[
                { name: 'testRunId', label: 'Test Run ID', sort: true, filter: true, class: style.smallColumn },
                {
                    name: 'testSuiteHandle',
                    label: 'Test Suite',
                    sort: false,
                    filter: true,
                },
                {
                    name: 'status',
                    label: 'Status',
                    sort: true,
                    filter: true,
                    filterSettings: {
                        type: 'selectbox',
                        placeholder: '-- Choose status --',
                        options: [
                            { label: 'No Data', value: 0 },
                            { label: 'In Progress', value: 1 },
                            { label: 'Success', value: 2 },
                            { label: 'Failure', value: 3 },
                        ],
                    },
                    render: (value, row) => {
                        let content;
                        switch (value) {
                            case 1: {
                                const twoHoursMs = 2 * 60 * 60 * 1000;
                                const isStalled =
                                    row.updatedAt && Date.now() - new Date(row.updatedAt).getTime() > twoHoursMs;
                                content = isStalled ? (
                                    <span>
                                        <span role="img" aria-label="stalled">
                                            ⚠️
                                        </span>
                                        {' Stalled'}
                                    </span>
                                ) : (
                                    <span>
                                        <IconSpinner spaceRight />
                                        In Progress
                                    </span>
                                );
                                break;
                            }
                            case 2:
                                content = (
                                    <span>
                                        <span className="text-success">
                                            <IconCheck spaceRight />
                                        </span>
                                        Success
                                    </span>
                                );
                                break;
                            case 3:
                                content = (
                                    <span>
                                        <span className="text-danger">
                                            <IconCross spaceRight />
                                        </span>
                                        Failed
                                    </span>
                                );
                                break;
                            default:
                                content = <span>-</span>;
                        }

                        return content;
                    },
                },
                {
                    name: 'testsRun',
                    label: 'Test Cases',
                    sort: false,
                    filter: true,
                    render: (value, row) => {
                        return (
                            <span>
                                {row.testsPassed} / {row.testsRun}
                            </span>
                        );
                    },
                },
                {
                    name: 'junit',
                    label: 'jUnit',
                    sort: false,
                    filter: true,
                    class: style.mediumColumn,
                    render: (value, row) => {
                        return (
                            <Modal
                                title={`Test Run updated on ${row.updatedAt}`}
                                trigger={
                                    <button type="button" className="btn btn-secondary">
                                        <IconText spaceRight />
                                        Junit
                                    </button>
                                }
                                size="calc(100vw - 80px)"
                                render={() => <PreOutput value={value} />}
                            />
                        );
                    },
                },
                {
                    name: 'console',
                    label: 'Output',
                    sort: false,
                    filter: true,
                    class: style.mediumColumn,
                    render: (value, row) => {
                        return (
                            <Modal
                                title={`Test Run updated on ${row.updatedAt}`}
                                trigger={
                                    <button type="button" className="btn btn-secondary">
                                        <IconText spaceRight />
                                        Console
                                    </button>
                                }
                                size="calc(100vw - 80px)"
                                render={() => <PreOutput value={value} />}
                            />
                        );
                    },
                },
                {
                    name: 'testSuiteHandle',
                    label: 'Traces',
                    sort: false,
                    filter: false,
                    class: style.mediumColumn,
                    render: (value, row) => {
                        if (row.status !== 3) {
                            return (
                                <button type="button" className="btn btn-secondary" disabled>
                                    <IconText spaceRight />
                                    Traces
                                </button>
                            );
                        }
                        return (
                            <Modal
                                title={`Traces: ${value}`}
                                size="calc(100vw - 80px)"
                                trigger={
                                    <button type="button" className="btn btn-secondary">
                                        <IconText spaceRight />
                                        Traces
                                    </button>
                                }
                                render={() => <TracesContent app={app} tag={tag} suite={value} />}
                            />
                        );
                    },
                },
                {
                    name: 'startedAt',
                    label: 'Started',
                    sort: true,
                    filter: true,
                    filterSettings: { type: 'date' },
                },
                {
                    name: 'updatedAt',
                    label: 'Last Updated',
                    sort: true,
                    filter: true,
                    filterSettings: { type: 'date' },
                },
            ]}
        />
    );
}

function TracesContent({ app, tag, suite }) {
    const [traces, setTraces] = useState(null);
    const [error, setError] = useState(null);

    React.useEffect(() => {
        axios
            .get('/api/monitoring/devops/tests/traces', { params: { apphandle: app, tag, suite } })
            .then(response => {
                if (response.data.status === 'success') {
                    setTraces(response.data.traces);
                } else {
                    setError('Could not load trace list.');
                }
            })
            .catch(() => setError('Could not load trace list.'));
    }, []);

    if (error) return <p className="text-muted p-2">{error}</p>;
    if (traces === null) return <Loader loading type="inline" />;
    if (traces.length === 0) return <p className="p-2">No trace files found for this suite.</p>;

    return (
        <ul className="list-unstyled p-2">
            {traces.map(url => {
                const testName = url.split('/trace/')[1]?.replace('/trace.zip', '') ?? url;
                const viewerUrl = `https://trace.playwright.dev/?trace=${url}`;
                return (
                    <li key={url} className="mb-2">
                        <a href={viewerUrl} target="_blank" rel="noopener noreferrer">
                            {testName}
                        </a>
                    </li>
                );
            })}
        </ul>
    );
}

function BuildLogContent({ app, tag, testStatus }) {
    const [log, setLog] = useState(null);
    const [error, setError] = useState(null);

    async function fetchLog() {
        const params = new URLSearchParams({ apphandle: app, tag });
        const resp = await fetch(`/api/monitoring/devops/tests/log?${params.toString()}`);
        const json = await resp.json();
        if (json.status === 'success') {
            setLog(json.log);
            setError(null);
        } else {
            setError(json.message || 'Could not load test runner log.');
        }
    }

    React.useEffect(() => {
        fetchLog();
        if (parseInt(testStatus, 10) < 2) {
            const interval = setInterval(fetchLog, 10000);
            return () => clearInterval(interval);
        }
    }, []);

    if (error) {
        return <p className="text-muted p-2">{error}</p>;
    }
    if (log === null) {
        return <Loader loading type="inline" />;
    }
    return (
        <pre
            className="p-2 font-monospace bg-body-secondary rounded"
            style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: '70vh', overflowY: 'auto' }}
        >
            {log}
        </pre>
    );
}

function BuildLogModal({ app, tag, testStatus }) {
    return (
        <Modal
            title={`Test Runner Log: ${app} @ ${tag}`}
            size="calc(100vw - 80px)"
            trigger={
                <button type="button" className="btn btn-secondary">
                    <IconText spaceRight />
                    Test Runner Log
                </button>
            }
            render={() => <BuildLogContent app={app} tag={tag} testStatus={testStatus} />}
        />
    );
}

async function fetchAllTestRuns(testableId, testableType, setData) {
    const resp = await client.query({
        query: TestRunQuery,
        variables: { testableId, testableType },
        fetchPolicy: 'network-only',
    });

    let allTestRuns = resp?.data?.monitoring?.devopsTestRunList || [];

    let translatedTestRuns = [];
    for (const testRun of allTestRuns) {
        for (const testSuite of testRun.testSuiteList) {
            let s;
            if (!testSuite.finished) {
                s = 1;
            } else if (testSuite.testsPassed === testSuite.testsRun) {
                s = 2;
            } else {
                s = 3;
            }

            let tr = {
                testRunKey: testRun.testRunId + '-' + testSuite.handle,
                testRunId: testRun.testRunId,
                testSuiteHandle: testSuite.handle,
                status: s,
                testsPassed: testSuite.testsPassed,
                testsRun: testSuite.testsRun,
                junit: testSuite.results,
                console: testSuite.console,
                startedAt: testRun.startedAt,
                updatedAt: testSuite.updatedAt,
            };
            translatedTestRuns.push(tr);
        }
    }
    // allTestRuns = allTestRuns.map(testRun => {
    //     testRun.consoleOutput = JSON.parse(testRun.consoleOutput);
    //     testRun.testSuites = JSON.parse(testRun.testSuites);

    //     // TBD format of testCases and testSuites
    //     if ('testCases' in testRun.testSuites && Array.isArray(testRun.testSuites.testCases)) {
    //         testRun.testCases = testRun.status === 2 ? '20/20 passed' : '19/20 passed';
    //     }

    //     if ('testSuites' in testRun.testSuites && Array.isArray(testRun.testSuites.testSuites)) {
    //         testRun.testSuites = testRun.status === 2 ? '5/5 passed' : '4/5 passed';
    //     }

    //     return testRun;
    // });
    allTestRuns = translatedTestRuns;
    setData(allTestRuns);
}

export const TestStatus = ({ value, row }) => {
    const [data, setData] = useState(null);

    let trigger = (
        <small>
            <i>-</i>
        </small>
    );

    if (!value && value !== 0) {
        return trigger;
    }

    const status = parseInt(value, 10);
    const linkProps = {
        href: '#',
        className: style.testStatusTrigger,
    };

    if (status === 1) {
        const updatedAt = _get(row, 'testRun.updatedAt');
        const twoHoursMs = 2 * 60 * 60 * 1000;
        const isStalled = updatedAt && Date.now() - new Date(updatedAt).getTime() > twoHoursMs;

        trigger = (
            <a {...linkProps}>
                {isStalled ? (
                    <span role="img" aria-label="stalled">
                        ⚠️
                    </span>
                ) : (
                    renderSpinner()
                )}
                <span>{isStalled ? ' Stalled' : 'In Progress'}</span>
            </a>
        );
    }

    if (status === 2) {
        trigger = (
            <a {...linkProps}>
                <span role="img" aria-label="success">
                    ✅
                </span>{' '}
                <span>Success</span>
            </a>
        );
    }

    if (status === 3) {
        trigger = (
            <a {...linkProps}>
                <span role="img" aria-label="failure">
                    ❌
                </span>{' '}
                <span>Failed</span>
            </a>
        );
    }

    const testableId = _get(row, 'testRun.testableId');
    const testableType = _get(row, 'testRun.testableType');
    if (!testableId || !testableType) {
        return trigger;
    }

    const testableDisplayName = testableType.slice(testableType.lastIndexOf('\\') + 1);

    let isTooltip = [1, 2, 3].includes(status);
    if (isTooltip) {
        trigger = (
            <Tooltip title={`See details about all Test Runs for this ${testableDisplayName}`}>{trigger}</Tooltip>
        );
    }

    return (
        <Modal
            title={`Test runs for ${testableDisplayName} #${testableId}`}
            size="calc(100vw - 80px)"
            trigger={trigger}
            render={({ closeModal }) => (
                <>
                    <div style={{ marginBottom: '12px' }}>
                        <BuildLogModal app={row.app} tag={row.tag} testStatus={status} />
                    </div>
                    <AllTestRuns closeModal={closeModal} data={data} app={row.app} tag={row.tag} />
                </>
            )}
            afterShow={() => {
                fetchAllTestRuns(testableId, testableType, setData);
            }}
        />
    );
};

export const ModalView = ({ value, row, columnName: col, columnRenderParams: params }) => {
    let buttontext = 'Open';
    let modaltitle = col;

    if (col in params) {
        if ('buttontext' in params[col]) {
            buttontext = params[col].buttontext;
        }
        if ('title' in params[col]) {
            modaltitle = params[col].title;
        }
    }
    if (row.status > 1) {
        return (
            <Modal
                trigger={
                    <button type="button" className="btn btn-secondary">
                        {buttontext}
                    </button>
                }
                size="1200px"
                title={modaltitle}
                render={({ closeModal }) => <p className={style.consoleModal}>{value}</p>}
            />
        );
    }
    return (
        <Modal
            trigger={
                <button type="button" className="btn btn-secondary">
                    {buttontext}
                </button>
            }
            size="1200px"
            title={modaltitle}
            render={({ closeModal }) => (
                <p className={style.consoleModal}>
                    {value}
                    <br />
                    <Spinner options={{ position: 'relative', top: '0.7em', left: '0.7em' }} />
                    <br />
                </p>
            )}
        />
    );
};

function formatSeconds(totalSeconds) {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const formattedHours = String(hours).padStart(2, '0');
    const formattedMinutes = String(minutes).padStart(2, '0');
    const formattedSeconds = String(seconds).padStart(2, '0');

    return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
}

export const Runtime = ({ value }) => {
    if (value > 0) {
        return formatSeconds(value);
    }
    return value;
};
